import { useEffect, useMemo, type ReactElement } from 'react';
import { Alert, Box, Button, Divider, Group, Loader, Modal, NumberInput, SegmentedControl, Select, Stack, Text, TextInput } from '@mantine/core';
import { Sparkles, Wand2 } from 'lucide-react';
import { ASSIST_METRICS, ASSIST_TIME_BUCKETS, type AssistContext, type AssistVizKind } from '@/shared/assist/AssistContract';
import type { AnalyticsField, AnalyticsFilter, AnalyticsMetric, TimeBucket } from '@/shared/analytics/AnalyticsTypes';
import { chartPlanFromTileSpec } from './PinnedTileTypes';
import type { AnalyticsSurface } from './AnalyticsSurface';
import type { AddTileViewModel } from './AddTileViewModel';
import { useFieldCatalog } from './FieldCatalogService';
import SurfaceAssistChart from './assist/SurfaceAssistChart';

const VIZ_OPTIONS = [
    { value: 'ranked-bars', label: 'Ranked bars' },
    { value: 'donut', label: 'Donut' },
    { value: 'trend', label: 'Trend' },
];
const METRIC_OPTIONS = ASSIST_METRICS.map((metric) => ({ value: metric.value, label: metric.label }));
const BUCKET_OPTIONS = ASSIST_TIME_BUCKETS.map((bucket) => ({ value: bucket, label: `${bucket.charAt(0).toUpperCase()}${bucket.slice(1)}ly` }));

interface AddTileModalProps {
    vm: AddTileViewModel;
    surface: AnalyticsSurface;
    pageFilters: AnalyticsFilter[];
    onSave: () => void;
}

/** The custom-tile builder: describe-to-configure, manual config, a live preview, and save. */
export default function AddTileModal({ vm, surface, pageFilters, onSave }: AddTileModalProps): ReactElement {
    const fields = useFieldCatalog();
    const fieldOptions = useMemo(() => fields.map((field) => ({ value: field.value, label: field.label })), [fields]);
    const describeContext: AssistContext = { surfaceId: surface.id, surfaceTitle: surface.title, topicId: undefined, activeFilters: pageFilters };
    const previewSig =
        vm.isComplete ? (vm.viz === 'trend' ? `trend|${vm.metric}|${vm.bucket}` : `${vm.viz}|${vm.primaryField}|${vm.metric}|${vm.limit}`) : 'incomplete';

    useEffect(() => {
        if (vm.modalOpen) void vm.runPreview(pageFilters);
    }, [vm, previewSig, pageFilters, vm.modalOpen]);

    const spec = vm.currentSpec();
    const previewPlan = spec !== null ? chartPlanFromTileSpec(spec) : null;
    const hasPreview = vm.aggregate !== null || vm.trend !== null;

    const isEditing = vm.editingTileId !== null;

    return (
        <Modal opened={vm.modalOpen} onClose={() => vm.closeModal()} title={isEditing ? 'Edit insight' : 'Add insight'} size="lg" centered>
            <Stack gap="md">
                <Box>
                    <Text size="xs" fw={700} tt="uppercase" c="dimmed" mb={6} style={{ letterSpacing: '0.06em' }}>
                        Describe it
                    </Text>
                    <Group gap="xs" wrap="nowrap" align="flex-start">
                        <TextInput
                            style={{ flex: 1 }}
                            leftSection={<Sparkles size={15} />}
                            placeholder="e.g. total tokens by department"
                            value={vm.describePrompt}
                            onChange={(event) => vm.setDescribePrompt(event.currentTarget.value)}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                    event.preventDefault();
                                    void vm.describe(describeContext);
                                }
                            }}
                            aria-label="Describe the insight"
                        />
                        <Button
                            leftSection={<Wand2 size={15} />}
                            variant="light"
                            loading={vm.describing}
                            disabled={vm.describePrompt.trim().length === 0}
                            onClick={() => void vm.describe(describeContext)}
                        >
                            Configure
                        </Button>
                    </Group>
                    {vm.describeError !== null && (
                        <Text size="xs" c="red" mt={6}>
                            {vm.describeError}
                        </Text>
                    )}
                </Box>

                <Divider label="or configure manually" labelPosition="center" />

                <TextInput
                    label="Title"
                    placeholder="Leave blank to auto-name"
                    value={vm.title}
                    onChange={(event) => vm.setTitle(event.currentTarget.value)}
                />

                <Box>
                    <Text size="sm" fw={600} mb={6}>
                        Visualization
                    </Text>
                    <SegmentedControl
                        fullWidth
                        data={VIZ_OPTIONS}
                        value={vm.viz ?? ''}
                        onChange={(value) => vm.setViz(value as AssistVizKind)}
                    />
                </Box>

                <Group grow align="flex-start">
                    {vm.viz === 'trend' ?
                        <Select
                            label="Bucket"
                            data={BUCKET_OPTIONS}
                            value={vm.bucket}
                            onChange={(value) => value !== null && vm.setBucket(value as TimeBucket)}
                            allowDeselect={false}
                        />
                    :   <Select
                            label="Break down by"
                            placeholder="Choose a field"
                            data={fieldOptions}
                            value={vm.primaryField}
                            onChange={(value) => value !== null && vm.setPrimaryField(value as AnalyticsField)}
                            allowDeselect={false}
                        />
                    }
                    <Select
                        label="Metric"
                        placeholder="Choose a metric"
                        data={METRIC_OPTIONS}
                        value={vm.metric}
                        onChange={(value) => value !== null && vm.setMetric(value as AnalyticsMetric)}
                        allowDeselect={false}
                    />
                    {vm.viz !== null && vm.viz !== 'trend' && (
                        <NumberInput
                            label="Show top"
                            value={vm.limit}
                            onChange={(value) => vm.setLimit(typeof value === 'number' ? value : vm.limit)}
                            min={1}
                            max={50}
                            allowDecimal={false}
                            clampBehavior="strict"
                        />
                    )}
                </Group>

                <Box>
                    <Text size="xs" fw={700} tt="uppercase" c="dimmed" mb={6} style={{ letterSpacing: '0.06em' }}>
                        Preview
                    </Text>
                    {!vm.isComplete || previewPlan === null ?
                        <Box
                            style={{
                                border: '1px dashed var(--bm-color-border)',
                                borderRadius: 'var(--bm-radius)',
                                padding: 'var(--mantine-spacing-xl)',
                                textAlign: 'center',
                            }}
                        >
                            <Text size="sm" c="dimmed">
                                Pick a visualization and metric to preview a chart.
                            </Text>
                        </Box>
                    : vm.previewLoading && !hasPreview ?
                        <Group justify="center" py="xl">
                            <Loader size="sm" />
                        </Group>
                    : vm.previewError !== null ?
                        <Alert color="red" variant="light">
                            {vm.previewError}
                        </Alert>
                    :   <SurfaceAssistChart plan={previewPlan} aggregate={vm.aggregate} trend={vm.trend} onDrill={() => {}} />
                    }
                </Box>

                <Group justify="flex-end" gap="sm">
                    <Button variant="default" onClick={() => vm.closeModal()}>
                        Cancel
                    </Button>
                    <Button onClick={onSave} disabled={!vm.isComplete}>
                        {isEditing ? 'Save changes' : 'Add to page'}
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
}
